import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { ChevronDown, FileText, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  HomeworkAnalysesPageData,
  PortalHomeworkAnalysis,
} from "../api/contracts";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  ClientPrimaryTabs,
  SegmentedNavigation,
} from "../components/PortalNavigation";
import { insightsNavigation } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";
import { formatPortalDateTime } from "../utils/dateTime";

function ProgressReportMarkdown({ markdown }: { markdown: string }) {
  return (
    <div className="cp-progress-report__markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeSanitize]}
        skipHtml
        components={{
          h1: ({ children }) => <h3>{children}</h3>,
          h2: ({ children }) => <h4>{children}</h4>,
          h3: ({ children }) => <h5>{children}</h5>,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

function ProgressReportCard({
  report,
  expanded,
  onToggle,
}: {
  report: PortalHomeworkAnalysis;
  expanded: boolean;
  onToggle: () => void;
}) {
  const contentId = `progress-report-${useId().replace(/:/g, "")}`;
  const formattedCreatedAt = formatPortalDateTime(report.createdAt);
  return (
    <article
      className="cp-progress-report-card"
      data-expanded={expanded || undefined}
    >
      <button
        type="button"
        className="cp-progress-report-card__summary"
        aria-expanded={expanded}
        aria-controls={contentId}
        aria-label={`${expanded ? "Collapse" : "Expand"} progress report generated ${formattedCreatedAt}`}
        onClick={onToggle}
      >
        <span className="cp-progress-report-card__icon" aria-hidden="true">
          <FileText />
        </span>
        <span className="cp-progress-report-card__details">
          <span className="cp-progress-report-card__title">
            Progress Report
          </span>
          <span className="cp-progress-report-card__date">
            Generated {formattedCreatedAt}
          </span>
          {report.triggerSource ? <small>{report.triggerSource}</small> : null}
        </span>
        <ChevronDown
          className="cp-progress-report-card__chevron"
          aria-hidden="true"
        />
      </button>
      {expanded ? (
        <div id={contentId} className="cp-progress-report-card__content">
          <ProgressReportMarkdown markdown={report.analysis} />
        </div>
      ) : null}
    </article>
  );
}

export function ProgressReportsPage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const workspace = useClientWorkspace();
  const request = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getHomeworkAnalyses(
            clinicianCode,
            patientId,
            signal,
            "team",
          )
        : clinicianPortalApi.getHomeworkAnalyses(clinicianCode, patientId, signal),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const historyState = usePortalData(request);
  const snapshot = getClinicianPortalClientSnapshot(patientId, workspace.accessScope);
  const [refreshedData, setRefreshedData] =
    useState<HomeworkAnalysesPageData | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(false);
  const generationControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    generationControllerRef.current?.abort();
    setRefreshedData(null);
    setSelectedReportId(null);
    setGenerating(false);
    setGenerationError(false);
    return () => generationControllerRef.current?.abort();
  }, [clinicianCode, patientId]);

  const historyData =
    historyState.status === "success" || historyState.status === "refreshing"
      ? historyState.data
      : undefined;
  const data = refreshedData ?? historyData;
  const available = Boolean(
    data?.found && data.authorized && data.therapist && data.patient,
  );
  const patient = available ? data?.patient : snapshot?.patient;
  const therapist = available ? data?.therapist : snapshot?.therapist;

  async function generateReport() {
    if (workspace.readOnly || generating) return;

    const controller = new AbortController();
    generationControllerRef.current?.abort();
    generationControllerRef.current = controller;
    setGenerating(true);
    setGenerationError(false);

    try {
      await clinicianPortalApi.generateHomeworkAnalysis(
        clinicianCode,
        patientId,
        controller.signal,
      );
      const latest = await clinicianPortalApi.getHomeworkAnalyses(
        clinicianCode,
        patientId,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setRefreshedData(latest);
    } catch {
      if (!controller.signal.aborted) setGenerationError(true);
    } finally {
      if (!controller.signal.aborted) setGenerating(false);
    }
  }

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Client",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="insights"
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--profile cp-page-content--compact-client cp-page-content--sticky-secondary-nav">
        <SegmentedNavigation
          items={insightsNavigation}
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="progress-reports"
        />

        {historyState.status === "loading" ? (
          <LoadingState label="Loading progress reports" />
        ) : null}
        {historyState.status === "error" && !refreshedData ? (
          <ErrorState error={historyState.error} onRetry={historyState.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Progress reports are not available")} />
        ) : null}

        {available && patient && data ? (
          <section aria-labelledby="progress-reports-heading">
            <div className="cp-content-header cp-content-header--route-intro">
              <div>
                <h2
                  id="progress-reports-heading"
                  className="cp-visually-hidden"
                >
                  Progress Reports
                </h2>
                <p>
                  Generate and review AI-assisted summaries of recent ERP
                  homework activity.
                </p>
              </div>
              {workspace.readOnly ? null : <div className="cp-content-header__actions">
                <button
                  type="button"
                  className="cp-button cp-button--primary"
                  disabled={generating}
                  onClick={generateReport}
                >
                  <Sparkles aria-hidden="true" />
                  {generating
                    ? "Generating report…"
                    : "Generate Progress Report"}
                </button>
              </div>}
            </div>

            {generationError ? (
              <div
                className="cp-inline-notice cp-inline-notice--error"
                role="alert"
              >
                <div>
                  Progress report could not be generated. Please try again.
                </div>
              </div>
            ) : null}

            {data.analyses.length ? (
              <div className="cp-progress-report-list">
                {data.analyses.map((report) => (
                  <ProgressReportCard
                    key={report.id}
                    report={report}
                    expanded={selectedReportId === report.id}
                    onToggle={() =>
                      setSelectedReportId((current) =>
                        current === report.id ? null : report.id,
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="cp-progress-reports-empty">
                <FileText aria-hidden="true" />
                <h3>No progress reports have been generated yet.</h3>
                <p>
                  Generate a progress report to summarize recent ERP homework
                  activity.
                </p>
              </div>
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
